import { Suspense, lazy } from 'react';
import { Navigate, createBrowserRouter, useLocation } from 'react-router-dom';
// layouts
import AuthGuardLayout from '../components/layouts/AuthGuardLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
// paths
import { INITIAL_PATH } from '../constants';
import { generalRoute } from 'src/features/general/routes/generalRoute';
import { historyRoute } from 'src/features/history/routes/historyRoute';
import { manageRoute } from 'src/features/manage/routes/manageRoute';
import { overviewRoute } from 'src/features/overview/routes';

// components
import ScrollToTop from 'src/components/ScrollToTop';
import LogoOnlyLayout from 'src/components/layouts/LogoOnlyLayout';
import Settings from 'src/components/settings';
import { RootErrorBoundary } from 'src/features/errors/RootErrorBoundary';
import { idIssuanceRoute } from 'src/features/id-issuance/routes';
import LoadingScreen from '../components/LoadingScreen';
import MfaSetGuard from 'src/guards/MfaSetGuard';
import { AddTotpDevice } from 'src/features/mfa/AddTotpDevice';
import { SelectMfaMethod } from 'src/features/mfa/SelectMfaMethod';
// ----------------------------------------------------------------------

const Loadable = (Component: any) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed',
            }),
          }}
        />
      }>
      <Component {...props} />
    </Suspense>
  );
};

const Login = Loadable(lazy(() => import('src/features/authentication/Login')));
const ResetPassword = Loadable(lazy(() => import('src/features/authentication/ResetPassword')));
const VerifyCode = Loadable(lazy(() => import('src/features/authentication/VerifyCode')));
const Page500 = Loadable(lazy(() => import('src/features/errors/Page500')));
const NotFound = Loadable(lazy(() => import('src/features/errors/Page404')));

export const router = createBrowserRouter([
  {
    errorElement: <RootErrorBoundary />,
    children: [
      {
        path: '/',
        element: (
          <>
            <AuthGuardLayout />,
            <Settings />
            <ScrollToTop />
          </>
        ),
        children: [{ path: '/', element: <Navigate to={INITIAL_PATH} replace /> }],
      },
      {
        path: 'auth',
        children: [
          {
            path: 'login',
            element: (
              <GuestGuard>
                <Login />
              </GuestGuard>
            ),
          },
          { path: 'reset-password', element: <ResetPassword /> },
          { path: 'verify', element: <VerifyCode /> },
          // MFA対応 start --------------
          {
            path: 'select-mfa-method',
            element: (
              <MfaSetGuard>
                <SelectMfaMethod />
              </MfaSetGuard>
            ),
          },
          {
            path: 'add-totp-device',
            element: (
              <MfaSetGuard>
                <AddTotpDevice />
              </MfaSetGuard>
            ),
          },
          // MFA対応 end --------------
        ],
      },
      // Feature Routes
      overviewRoute,
      generalRoute,
      historyRoute,
      manageRoute,
      idIssuanceRoute,
      {
        path: '*',
        element: <LogoOnlyLayout />,
        children: [
          { path: '500', element: <Page500 /> },
          { path: '404', element: <NotFound /> },
          { path: '*', element: <Navigate to="/404" replace /> },
        ],
      },
    ],
  },
]);
